/**
 * CapRing.tsx
 * Circular cap space indicator showing remaining budget
 * 
 * Single Responsibility: Display remaining cap as animated ring
 * Props In: used, total
 * Events Out: none
 * 
 * Reference: TunnelMockupsV5_Final.jsx lines 67-93
 */
import React from 'react';
import { ClipboardList } from 'lucide-react';

interface CapRingProps {
  used: number;
  total: number;
}

export const CapRing: React.FC<CapRingProps> = ({ used, total }) => {
  const remaining = total - used;
  const percentage = (remaining / total) * 100;
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  // Color thresholds: green > 50%, yellow > 20%, red <= 20%
  const ringColor = percentage > 50 ? '#10B981' : percentage > 20 ? '#F59E0B' : '#EF4444';
  
  return (
    <div className="flex items-center gap-2">
      <ClipboardList className="w-4 h-4 text-slate-500" />
      <div className="relative flex items-center justify-center">
        <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
          {/* Background ring */}
          <circle 
            cx="22" 
            cy="22" 
            r="18" 
            fill="none" 
            stroke="#334155" 
            strokeWidth="3" 
          />
          {/* Progress ring */}
          <circle 
            cx="22" 
            cy="22" 
            r="18" 
            fill="none" 
            stroke={ringColor} 
            strokeWidth="3"
            strokeLinecap="round" 
            strokeDasharray={circumference} 
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        {/* Center text */}
        <div className="absolute flex flex-col items-center justify-center">
          <span className="font-mono text-sm font-black text-white leading-none">
            {remaining}
          </span>
          <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wide">
            Cap
          </span>
        </div>
      </div>
    </div>
  );
};

export default CapRing;
